import { ArpMediaType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTweetArpMedia } from '../../../__legacy__/components/detail-pages/shared/TweetButton'
import { useRemix } from '../../../__legacy__/components/platform/explore/common/utils'
import { SongsTable } from '../../../__legacy__/components/platform/new-explore/components/Table'
import Tabs from '../../../__legacy__/components/platform/new-explore/components/Tabs'
import { SearchContext } from '../../../__legacy__/components/platform/new-explore/hooks/useSearch'
import { useAuth } from '../../../__legacy__/hooks/useAuth'
import { useTreeState } from '../../../__legacy__/stores/Root'

import { IArpMedia } from '../../arp-registry/ArpMedia'
import { ArpTable } from '../../arp-table'
import { TableRow } from '../../arp-tablerow'
import { EmptyState } from '../../empty-state'
import { CurrentPageContext } from '../../verified-media'
import { useArpRemixFilters, useCSSGradient } from '../hooks'
import { Section } from '../primitives'

const { Headers } = ArpTable

const SongRemixTable: FC<{ id: number; emptyState?: ReactNode }> = observer(
  ({ id, emptyState }) => {
    const { globalPlayer } = useTreeState()
    const currentId = globalPlayer.arpMediaIndex
    return (
      <ArpTable.SongRemixRoot id={id}>
        <Headers.Root>
          <Headers.UnsortableTitle />
          <Headers.Tags />
          <Headers.UnsortableDateMinted />
          <Headers.UnsortablePlays />
          <Headers.UnsortableFavorite />
          <Headers.UnsortableDuration />
        </Headers.Root>
        <ArpTable.Table emptyState={emptyState}>
          {(media) => (
            <TableRow.Root media={media} key={media.id} active={currentId === media.id}>
              <TableRow.Main />
              <TableRow.Tags />
              <TableRow.DateMinted />
              <TableRow.Plays />
              <TableRow.Favorite />
              <TableRow.Duration />
              <TableRow.Actions />
            </TableRow.Root>
          )}
        </ArpTable.Table>
      </ArpTable.SongRemixRoot>
    )
  }
)

export const ArpRemixesTabContent: FC<{ media: IArpMedia }> = observer(({ media }) => {
  const { currentWalletAddress } = useAuth()
  const { remixSong, remixStem, remixSample } = useRemix()
  const { tweet, status: tweetStatus } = useTweetArpMedia(media.id, media.type)
  const navigate = useNavigate()
  const filters = useArpRemixFilters(media)
  const search = useState()
  const { color } = useCSSGradient(media)
  const mediaBelongsToUser = currentWalletAddress === media.user.walletAddress

  const onEmptyClick = () => {
    if (mediaBelongsToUser) return tweet()
    else if (media.type === ArpMediaType.SONG) return remixSong(media.id)
    else if (media.type === ArpMediaType.STEM) return remixStem(media.id)
    else if (media.type === ArpMediaType.SAMPLE) return remixSample(media.id)
    return navigate('/studio')
  }

  const emptyStateSongs = (
    <EmptyState.Root>
      <EmptyState.Icon />
      <EmptyState.Text>Be the first to remix this sound</EmptyState.Text>
      <EmptyState.ButtonRow>
        <EmptyState.PrimaryButton onClick={onEmptyClick}>
          {mediaBelongsToUser ? 'Share your sound' : 'Open in studio'}
        </EmptyState.PrimaryButton>
      </EmptyState.ButtonRow>
    </EmptyState.Root>
  )

  return (
    <Tabs.Content value="#remixes">
      <Section.Root>
        <Section.Flare1 css={{ color }} />
        <Section.Flare2 css={{ color }} />
        <Section.Content>
          <SearchContext.Provider value={search}>
            <CurrentPageContext.Provider value={{ arpMediaId: media.id }}>
              {media.type === ArpMediaType.SONG ? (
                <SongRemixTable id={media.id} emptyState={emptyStateSongs} />
              ) : (
                <SongsTable defaultFilters={filters} emptyState={emptyStateSongs} />
              )}
            </CurrentPageContext.Provider>
          </SearchContext.Provider>
        </Section.Content>
      </Section.Root>
    </Tabs.Content>
  )
})
